import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Card, CardContent, Container, Paper, Typography } from "@mui/material";

import Seo from "../components/Seo";
import { SITE } from "../data/site";
import { services } from "../data/services";
import { buildBreadcrumbSchema, buildItemListSchema, buildWebPageSchema } from "../utils/schema";

import "./Services.css";

export default function Services() {
  const safeServices = Array.isArray(services) ? services : [];
  const title = "Services — Brainwave Solutions";
  const description =
    "Practical help with spreadsheets, dashboards, data cleanup, automation, websites, SEO, bookkeeping, operations support, and custom tools.";
  const canonical = "https://brainwavesolutions.net/services";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical, type: "CollectionPage" }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: canonical },
    ]),
    buildItemListSchema({
      name: "Brainwave Solutions services",
      items: safeServices.map((service) => ({
        name: service.title,
        url: `${SITE.url}${service.uri}`,
      })),
    }),
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      {/* Page header */}
      <Box className="bws-servicesHero">
        <Container maxWidth="lg">
          <div className="bws-servicesHeroInner">
            <div>
              <Typography variant="h1" className="bws-servicesTitle">
                Services
              </Typography>

              <p className="bws-servicesLead">
                Brainwave Solutions helps businesses and individuals clean up messy work, improve reporting, organize
                data, automate recurring tasks, strengthen websites, and build practical tools that make things easier
                to run.
              </p>

              <p className="bws-servicesLead bws-servicesLeadSecondary">
                Some projects are quick fixes. Others are larger systems, ongoing support, or a better workflow
                altogether. Either way, the goal is the same: reduce friction, improve clarity, and give you something
                that works the way it should.
              </p>

              <div className="bws-servicesBadges">
                <span className="bws-badge">Businesses &amp; individuals</span>
                <span className="bws-badge">Quick fixes &amp; full projects</span>
                <span className="bws-badge">Direct communication</span>
              </div>

              <div className="bws-servicesTopCta">
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  className="bws-btn"
                  sx={{ minWidth: "125px" }}
                >
                  Contact
                </Button>
              </div>
            </div>

            <Paper className="bws-servicesSideCard" elevation={0}>
              <Typography className="bws-servicesSideTitle">How it works</Typography>
              <ol className="bws-servicesSideList">
                <li>Send a quick description of the problem, project, or outcome you want.</li>
                <li>I’ll review it and suggest the best path forward.</li>
              </ol>

              <Typography className="bws-muted bws-servicesSideNote">
                Not sure how to describe it? Screenshots, sample files, or a rough explanation are perfectly fine.
              </Typography>

              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                className="bws-btn"
                sx={{ mt: 1 }}
              >
                Start a conversation
              </Button>
            </Paper>
          </div>
        </Container>
      </Box>

      {/* Service list */}
      <Box className="bws-servicesSection">
        <Container maxWidth="lg">
          <div className="bws-sectionHead">
            <Typography variant="h2" className="bws-sectionTitle">
              What I can help with
            </Typography>
            <p className="bws-muted">
              These services are flexible — if your situation does not fit neatly into one category, I can still help
              you find the right approach.
            </p>
          </div>

          <div className="bws-servicesGrid">
            {safeServices.map((s) => (
              <Card key={s.title} className="bws-serviceCard" elevation={0}>
                <CardContent
                  className="bws-serviceCardContent"
                  component={RouterLink}
                  to={s.uri}
                  sx={{ textDecoration: "none", color: "inherit", display: "block" }}
                >
                  <Typography className="bws-serviceTitle">{s.title}</Typography>
                  <Typography className="bws-serviceBlurb">{s.blurb}</Typography>

                  <ul className="bws-serviceList">
                    {(Array.isArray(s.bullets) ? s.bullets : []).map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>

                  <div className="bws-serviceCtas">
                    <span className="bws-serviceLinkText">Learn more →</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Paper className="bws-servicesBottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Not seeing your exact need?</Typography>
              <p className="bws-muted">
                That is normal. Send a quick description of what is not working and what you want the end result to be,
                and I’ll recommend the best direction.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
